import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import api from '@/lib/api';
import type { SharedTaskPriority, SharedTaskCategory } from '@/types';

const CATEGORIES: { value: SharedTaskCategory; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'household', label: 'Household' },
  { value: 'kai_care', label: "Kai's Care" },
  { value: 'shopping', label: 'Shopping' },
  { value: 'medical', label: 'Medical' },
];

const PRIORITIES: { value: SharedTaskPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: 'text-gray-400' },
  { value: 'normal', label: 'Normal', color: 'text-blue-400' },
  { value: 'high', label: 'High', color: 'text-orange-400' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-400' },
];

interface AddTaskFormProps {
  onClose: () => void;
}

export function AddTaskForm({ onClose }: AddTaskFormProps) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<SharedTaskCategory>('general');
  const [priority, setPriority] = useState<SharedTaskPriority>('normal');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [showExtras, setShowExtras] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get('/users');
      return res.data.users || res.data || [];
    },
    staleTime: 1000 * 30,
  });

  const createTask = useMutation({
    mutationFn: async () => {
      const data: any = { title, category, priority };
      if (assignedTo) data.assigned_to = assignedTo;
      if (dueDate) data.due_date = dueDate;
      if (notes.trim()) data.notes = notes.trim();
      return api.post('/shared-tasks', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['shared-tasks-counts'] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    createTask.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card rounded-xl border border-slate-700 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-white">New Task</h3>
        <button type="button" onClick={onClose} className="p-1 hover:bg-slate-700 rounded transition-colors">
          <X size={16} className="text-gray-400" />
        </button>
      </div>

      {/* Title */}
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="What needs to be done?"
        autoFocus
        className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {/* Category + Priority row */}
      <div className="flex gap-2">
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as SharedTaskCategory)}
          className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value as SharedTaskPriority)}
          className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {PRIORITIES.map((p) => (
            <option key={p.value} value={p.value}>{p.label}</option>
          ))}
        </select>
      </div>

      {/* Extra fields toggle */}
      {!showExtras && (
        <button
          type="button"
          onClick={() => setShowExtras(true)}
          className="text-xs text-gray-400 hover:text-gray-300"
        >
          + Add assignee, due date, or notes
        </button>
      )}

      {showExtras && (
        <div className="space-y-2">
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Unassigned</option>
            {(users as any[]).map((u: any) => (
              <option key={u.id} value={u.id}>
                {u.emoji || ''} {u.first_name} {u.last_name || ''}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            rows={2}
            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={!title.trim() || createTask.isPending}
        className="w-full py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        {createTask.isPending ? 'Adding...' : 'Add Task'}
      </button>
    </form>
  );
}
